// ===========================================
// Compagnies — Page liste des compagnies partenaires (refonte UX)
// ===========================================
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
    Table, Card, Input, Button, Grid, Modal, Form, Popconfirm, Space, Tooltip, Tag, Empty,
} from 'antd';
import {
    SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, BankOutlined,
    PhoneOutlined, MailOutlined, EnvironmentOutlined, IdcardOutlined,
} from '@ant-design/icons';
import { compagniesService } from '../../common/services';

import MiniKpiCard from '../clients/components/MiniKpiCard';
import Can from '../../components/can';

import './compagnies.page.scss';

const getInitials = (nom) =>
    (nom || '')
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w[0])
        .join('')
        .substring(0, 2)
        .toUpperCase();

const Compagnies = () => {
    const [compagnies, setCompagnies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [modalVisible, setModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [form] = Form.useForm();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    const fetchCompagnies = async () => {
        setLoading(true);
        try {
            const res = await compagniesService.getAll();
            const data = Array.isArray(res) ? res : res?.data || [];
            setCompagnies(data);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchCompagnies(); }, []);

    const filteredCompagnies = useMemo(() => {
        if (!search) return compagnies;
        const q = search.toLowerCase();
        return compagnies.filter((c) =>
            `${c.nom || ''} ${c.code || ''} ${c.telephone || ''} ${c.email || ''} ${c.adresse || ''}`
                .toLowerCase()
                .includes(q)
        );
    }, [compagnies, search]);

    const stats = useMemo(() => {
        const total = compagnies.length;
        const avecEmail = compagnies.filter((c) => !!c.email).length;
        const avecTel = compagnies.filter((c) => !!c.telephone).length;
        return { total, avecEmail, avecTel };
    }, [compagnies]);

    const handleSave = async (values) => {
        try {
            setModalLoading(true);
            if (editingItem) {
                await compagniesService.update(editingItem._id, values);
                toast.success('Compagnie mise à jour');
            } else {
                await compagniesService.create(values);
                toast.success('Compagnie créée');
            }
            setModalVisible(false);
            setEditingItem(null);
            form.resetFields();
            fetchCompagnies();
        } catch (error) {
            toast.error(error.message || 'Erreur');
        } finally {
            setModalLoading(false);
        }
    };

    const handleEdit = (record) => {
        setEditingItem(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await compagniesService.delete(id);
            toast.success('Compagnie supprimée');
            fetchCompagnies();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const openCreateModal = () => {
        setEditingItem(null);
        form.resetFields();
        setModalVisible(true);
    };

    const columns = [
        {
            title: 'Compagnie',
            dataIndex: 'nom',
            key: 'nom',
            sorter: (a, b) => (a.nom || '').localeCompare(b.nom || ''),
            render: (t) => (
                <div className="compagnie-cell">
                    <div className="compagnie-logo">{getInitials(t) || <BankOutlined />}</div>
                    <span className="compagnie-name">{t || '—'}</span>
                </div>
            ),
        },
        {
            title: 'Code',
            dataIndex: 'code',
            key: 'code',
            width: 130,
            render: (t) => t
                ? <Tag color="geekblue" className="compagnie-code-tag">{t}</Tag>
                : <span className="compagnie-muted">—</span>,
        },
        {
            title: 'Téléphone',
            dataIndex: 'telephone',
            key: 'telephone',
            width: 160,
            render: (t) => t ? (
                <a href={`tel:${t}`} className="compagnie-link">
                    <PhoneOutlined /> {t}
                </a>
            ) : <span className="compagnie-muted">—</span>,
        },
        {
            title: 'Email',
            dataIndex: 'email',
            key: 'email',
            ellipsis: true,
            render: (t) => t ? (
                <a href={`mailto:${t}`} className="compagnie-link">
                    <MailOutlined /> {t}
                </a>
            ) : <span className="compagnie-muted">—</span>,
        },
        {
            title: 'Adresse',
            dataIndex: 'adresse',
            key: 'adresse',
            ellipsis: true,
            render: (t) => t ? (
                <span className="compagnie-addr">
                    <EnvironmentOutlined /> {t}
                </span>
            ) : <span className="compagnie-muted">—</span>,
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            align: 'center',
            fixed: isMobile ? false : 'right',
            render: (_, record) => (
                <Space size={6}>
                    <Can perm="compagnies:update">
                        <Tooltip title="Modifier">
                            <Button
                                size="small"
                                icon={<EditOutlined />}
                                className="compagnie-action-btn compagnie-action-edit"
                                aria-label="Modifier cette compagnie"
                                onClick={() => handleEdit(record)}
                            />
                        </Tooltip>
                    </Can>
                    <Can perm="compagnies:delete">
                        <Popconfirm
                            title="Supprimer cette compagnie ?"
                            description="Cette action est irréversible."
                            okText="Oui"
                            cancelText="Non"
                            okButtonProps={{ danger: true }}
                            onConfirm={() => handleDelete(record._id)}
                        >
                            <Tooltip title="Supprimer">
                                <Button
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    className="compagnie-action-btn compagnie-action-delete"
                                    aria-label="Supprimer cette compagnie"
                                />
                            </Tooltip>
                        </Popconfirm>
                    </Can>
                </Space>
            ),
        },
    ];

    return (
        <div className="compagnies-page">
            {/* Header */}
            <header className="compagnies-header">
                <div className="compagnies-header-text">
                    <h1 className="compagnies-title">
                        <BankOutlined /> Compagnies partenaires
                    </h1>
                    <p className="compagnies-subtitle">Gestion des assureurs partenaires du courtage</p>
                </div>
                <div className="compagnies-header-actions">
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={openCreateModal}
                        className="compagnies-btn-add"
                    >
                        {!isMobile && 'Nouvelle compagnie'}
                    </Button>
                </div>
            </header>

            {/* KPIs */}
            <section className="compagnies-kpis" aria-label="Indicateurs compagnies">
                <MiniKpiCard
                    icon={<BankOutlined />}
                    label="Total compagnies"
                    value={stats.total}
                    tone="blue"
                />
                <MiniKpiCard
                    icon={<MailOutlined />}
                    label="Avec email"
                    value={stats.avecEmail}
                    tone="indigo"
                />
                <MiniKpiCard
                    icon={<PhoneOutlined />}
                    label="Avec téléphone"
                    value={stats.avecTel}
                    tone="green"
                />
            </section>

            {/* Carte principale */}
            <Card className="compagnies-main-card">
                <div className="compagnies-toolbar">
                    <Input
                        placeholder="Rechercher une compagnie (nom, code, téléphone, email)…"
                        prefix={<SearchOutlined />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        allowClear
                        className="compagnies-search"
                    />
                </div>

                <Table
                    dataSource={filteredCompagnies}
                    columns={columns}
                    rowKey="_id"
                    loading={{ spinning: loading, size: 'large', tip: 'Chargement…' }}
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: loading ? <div style={{ minHeight: 220 }} /> : <Empty description="Aucune compagnie" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                    }}
                    pagination={{
                        pageSize: 10,
                        showTotal: (t) => `${t} compagnie${t > 1 ? 's' : ''}`,
                        showSizeChanger: true,
                    }}
                />
            </Card>

            {/* Modal créer / modifier */}
            <Modal
                title={
                    <span className="compagnies-modal-title">
                        <BankOutlined /> {editingItem ? 'Modifier la compagnie' : 'Nouvelle compagnie'}
                    </span>
                }
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                destroyOnClose
                width={520}
                className="compagnies-modal"
            >
                <Form form={form} layout="vertical" onFinish={handleSave} className="compagnies-form">
                    <Form.Item
                        name="nom"
                        label="Nom de la compagnie"
                        rules={[{ required: true, message: 'Le nom de la compagnie est obligatoire' }]}
                        normalize={(v) => v?.toUpperCase()}
                    >
                        <Input
                            autoFocus
                            prefix={<BankOutlined />}
                            placeholder="Ex : ATLANTA ASSURANCE"
                        />
                    </Form.Item>
                    <Form.Item
                        name="code"
                        label="Code"
                        normalize={(v) => v?.toUpperCase()}
                    >
                        <Input prefix={<IdcardOutlined />} placeholder="Ex : ATLANTA" />
                    </Form.Item>
                    <Form.Item
                        name="telephone"
                        label="Téléphone"
                        rules={[{ pattern: /^[0-9]{10}$/, message: 'Le numéro doit contenir 10 chiffres' }]}
                    >
                        <Input addonBefore="+225" maxLength={10} placeholder="0700000000" />
                    </Form.Item>
                    <Form.Item
                        name="email"
                        label="Email"
                        rules={[{ type: 'email', message: "Format d'email invalide" }]}
                        normalize={(v) => v?.toLowerCase().trim()}
                    >
                        <Input prefix={<MailOutlined />} placeholder="contact@compagnie.ci" />
                    </Form.Item>
                    <Form.Item name="adresse" label="Adresse">
                        <Input prefix={<EnvironmentOutlined />} placeholder="Adresse postale" />
                    </Form.Item>
                    <div className="compagnies-form-actions">
                        <Button onClick={() => setModalVisible(false)} className="compagnies-btn-cancel">
                            Annuler
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={modalLoading}
                            className="compagnies-btn-submit"
                        >
                            {editingItem ? 'Enregistrer' : 'Créer la compagnie'}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default Compagnies;
